import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, CheckCircle, Clock, ArrowRight, AlertCircle, RefreshCw, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  commercial_property: "🏢",
  workers_comp: "👷",
  general_liability: "⚖️",
  commercial_auto: "🚗",
  habitational: "🏠",
};

const STATUS_CONFIG = {
  pending:   { label: "Not Started",  color: "text-slate-500", bg: "bg-slate-50",  border: "border-slate-200", icon: Clock },
  in_progress: { label: "In Progress", color: "text-blue-600",  bg: "bg-blue-50",   border: "border-blue-200",  icon: RefreshCw },
  completed: { label: "Completed",    color: "text-green-600", bg: "bg-green-50",  border: "border-green-200", icon: CheckCircle },
  finalized: { label: "Finalized",    color: "text-green-700", bg: "bg-green-50",  border: "border-green-200", icon: CheckCircle },
};

export default function InsuredHub() {
  const { hubToken } = useParams();
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/inspections/hub/${hubToken}`)
      .then(r => setHub(r.data))
      .catch(() => setError("Survey hub not found. This link may have expired or is invalid."))
      .finally(() => setLoading(false));
  }, [hubToken]);

  const downloadConsolidatedReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await axios.get(`${API}/api/reports/consolidated/hub/${hubToken}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `RiskSurvey_Consolidated_Report_${hub?.insured_name?.replace(/\s/g, "_") || "report"}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Unable to download report. Please try again.");
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <RefreshCw className="w-6 h-6 animate-spin text-navy" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="text-center max-w-sm">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-slate-700 font-medium">{error}</p>
      </div>
    </div>
  );

  const completed = hub.surveys.filter(s => ["completed", "finalized"].includes(s.status)).length;
  const total = hub.surveys.length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-navy text-white px-6 py-5">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-heading font-bold text-base">Loss Control Survey Hub</p>
            <p className="text-white/60 text-xs">RiskSurvey.ai</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Property info */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h1 className="font-heading font-bold text-xl text-slate-800 mb-1" data-testid="hub-insured-name">
            Hello, {hub.insured_name}
          </h1>
          <p className="text-sm text-slate-500">{hub.property_address}{hub.city ? `, ${hub.city}` : ""}{hub.state ? `, ${hub.state}` : ""}</p>
          {hub.due_date && (
            <p className="text-xs text-amber-600 font-medium mt-2">
              Due: {new Date(hub.due_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </p>
          )}
        </div>

        {/* Progress */}
        {hub.all_complete ? (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex items-center gap-3" data-testid="hub-complete">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-green-800">All surveys complete!</p>
                <p className="text-sm text-green-600">Thank you. Your loss control information has been submitted.</p>
              </div>
            </div>
            <button
              onClick={downloadConsolidatedReport}
              disabled={downloadingReport}
              data-testid="download-consolidated-report-btn"
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-navy text-white rounded-2xl text-sm font-semibold hover:bg-navy/90 transition-colors disabled:opacity-60"
            >
              <Download className="w-4 h-4" />
              {downloadingReport ? "Generating Report..." : "Download Full Loss Control Report"}
            </button>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5" data-testid="hub-progress">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-blue-800">{completed} of {total} surveys completed</p>
              <span className="text-xs text-blue-600">{Math.round((completed / total) * 100)}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(completed / total) * 100}%` }} />
            </div>
          </div>
        )}

        {/* Survey cards */}
        <div className="space-y-3" data-testid="hub-survey-list">
          {hub.surveys.map((survey, i) => {
            const cfg = STATUS_CONFIG[survey.status] || STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            const isDone = ["completed", "finalized"].includes(survey.status);
            return (
              <div key={survey.id}
                className={`bg-white border ${cfg.border} rounded-2xl p-5 transition-all duration-200 ${!isDone ? "hover:shadow-md cursor-pointer" : ""}`}
                onClick={() => !isDone && window.open(survey.survey_url, "_blank")}
                data-testid={`hub-survey-${survey.inspection_type}`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-2xl w-10 text-center flex-shrink-0">
                    {TYPE_ICONS[survey.inspection_type] || "📋"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm">{survey.type_label}</p>
                    <div className={`inline-flex items-center gap-1 mt-1 text-xs font-medium ${cfg.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {cfg.label}
                    </div>
                  </div>
                  {!isDone && (
                    <a href={survey.survey_url} target="_blank" rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white rounded-xl text-xs font-semibold hover:bg-navy/90 transition-colors whitespace-nowrap"
                      data-testid={`start-survey-${survey.inspection_type}`}>
                      {survey.status === "in_progress" ? "Continue" : "Start"} <ArrowRight className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-slate-400">
          If you need assistance, contact your insurance representative directly.
        </p>
      </div>
    </div>
  );
}
